import type { TokenCredential } from "@azure/identity";
import { authedJson } from "./http.js";
import { LocalCliError } from "./errors.js";

const ARM = "https://management.azure.com";
const ARM_SCOPE = "https://management.azure.com/.default";
const STORAGE_API = "2023-05-01";

interface StorageList {
  value?: { name?: string; id?: string; properties?: { primaryEndpoints?: { table?: string } }; tags?: Record<string, string> }[];
}

/** Table endpoint + resource id of the storage account that holds the Metadata table. */
export async function discoverStampStorage(opts: {
  credential: TokenCredential;
  subscriptionId: string;
  resourceGroup: string;
}): Promise<{ tableEndpoint: string; accountResourceId: string; accountName: string }> {
  const list = (await authedJson<StorageList>({
    credential: opts.credential,
    scope: ARM_SCOPE,
    method: "GET",
    url: `${ARM}/subscriptions/${opts.subscriptionId}/resourceGroups/${opts.resourceGroup}/providers/Microsoft.Storage/storageAccounts?api-version=${STORAGE_API}`,
  })) ?? {};
  const accounts = (list.value ?? []).filter((a) => a.properties?.primaryEndpoints?.table);
  // The gateway's data storage account (which holds the Metadata table) is tagged
  // `m8t: storage` by the deploy (deploy/main.bicep). Prefer it explicitly —
  // a bare accounts[0] can grab the installer's storage account instead. Only fall
  // back to a lone candidate; refuse to guess among multiple untagged accounts.
  const chosen =
    accounts.find((a) => a.tags?.m8t === "storage") ??
    accounts.find((a) => a.tags?.["m8t:role"] === "gateway" || a.tags?.["m8t:role"] === "ledger") ??
    (accounts.length === 1 ? accounts[0] : undefined);
  const tableEndpoint = chosen?.properties?.primaryEndpoints?.table?.replace(/\/$/, "");
  if (!tableEndpoint || !chosen?.id || !chosen.name) {
    throw new LocalCliError({
      code: "PLATFORM_STAMP_STORAGE_NOT_FOUND",
      message: `No table-capable storage account found in resource group ${opts.resourceGroup}.`,
      hint: `Found: ${accounts.map((a) => a.name).join(", ") || "(none)"}.`,
    });
  }
  return { tableEndpoint, accountResourceId: chosen.id, accountName: chosen.name };
}
